import axiosInstance from 'shared/services/api/axiosInstance';
import { getSelectedTeamNumericId } from 'shared/utils/selectedTeamId';

const teamHeaders = () => {
  const id = getSelectedTeamNumericId();
  return id != null ? { 'X-Team-Id': String(id) } : {};
};

// integrations endpoint resolves team from query param, others from the header.
const teamParam = () => {
  const id = getSelectedTeamNumericId();
  return id != null ? { team_id: id } : {};
};

export async function listPlaygrounds() {
  const { data } = await axiosInstance.get('/playgrounds', { headers: teamHeaders() });
  return data.playgrounds || [];
}

export async function createPlayground(name) {
  const { data } = await axiosInstance.post('/playgrounds', { name }, { headers: teamHeaders() });
  return data.playground;
}

export async function getPlayground(id) {
  const { data } = await axiosInstance.get(`/playgrounds/${id}`, { headers: teamHeaders() });
  return data.playground;
}

export async function renamePlayground(id, name) {
  const { data } = await axiosInstance.patch(`/playgrounds/${id}`, { name }, { headers: teamHeaders() });
  return data.playground;
}

// Autosave the editor's full bench state (restored verbatim on next open).
export async function savePlaygroundState(id, state) {
  const { data } = await axiosInstance.put(`/playgrounds/${id}/state`, { state }, { headers: teamHeaders() });
  return data;
}

export async function deletePlayground(id) {
  const { data } = await axiosInstance.delete(`/playgrounds/${id}`, { headers: teamHeaders() });
  return data;
}

export async function listVersions(id) {
  const { data } = await axiosInstance.get(`/playgrounds/${id}/versions`, { headers: teamHeaders() });
  return data.versions || [];
}

export async function saveVersion(id, payload) {
  const { data } = await axiosInstance.post(`/playgrounds/${id}/versions`, payload, { headers: teamHeaders() });
  return data.version;
}

export async function rollbackVersion(id, versionNo) {
  const { data } = await axiosInstance.post(
    `/playgrounds/${id}/versions/${versionNo}/rollback`,
    {},
    { headers: teamHeaders() },
  );
  return data.version;
}

export async function runPlayground(payload) {
  const { data } = await axiosInstance.post('/playgrounds/run', payload, { headers: teamHeaders() });
  return data.results;
}

// Unified run — any input mode, recorded in the playground's run history.
export async function runPlaygroundRun(id, payload) {
  const { data } = await axiosInstance.post(`/playgrounds/${id}/run`, payload, { headers: teamHeaders() });
  return data; // { results, run }
}

// Comparison bench — run every panel once (generate-only, no scoring). Returns
// { results: { panels: [{ output, ok, error, usage, cost_usd, latency_ms }] }, run }.
// `score` opts into on-demand AI Scoring for this run. It is off unless the
// user turns it on, because scoring costs embeddings + an NLI pass per output.
export async function runComparePanels(id, panels, { score = false } = {}) {
  const { data } = await axiosInstance.post(
    `/playgrounds/${id}/run-compare`,
    { panels, score },
    { headers: teamHeaders() },
  );
  return data;
}

// Running a prompt over a dataset is a dataset agent (Label ▾ on the dataset
// page: Compare models). The Playground is single-run only.

export async function listRuns(id) {
  const { data } = await axiosInstance.get(`/playgrounds/${id}/runs`, { headers: teamHeaders() });
  return data.runs || [];
}

export async function getRun(id, runNo) {
  const { data } = await axiosInstance.get(`/playgrounds/${id}/runs/${runNo}`, { headers: teamHeaders() });
  return data.run;
}

export async function improveOutput(payload) {
  const { data } = await axiosInstance.post('/playgrounds/improve', payload, { headers: teamHeaders() });
  return data; // { suggestions: [{issue, suggestion}] | null }
}

export async function importSpan(spanId) {
  const { data } = await axiosInstance.get(`/playgrounds/import-span/${encodeURIComponent(spanId)}`, {
    headers: teamHeaders(),
  });
  return data.imported;
}

export async function listIntegrations() {
  const { data } = await axiosInstance.get('/evals/integrations', {
    headers: teamHeaders(),
    params: teamParam(),
  });
  // Chat models only (the server's default); decision models have their own picker.
  return (data.integrations || []).filter((i) => i.kind !== 'decision');
}
